import {
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import type { ProcessStepDto, SiteSettingsDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { useScrollPin } from '../hooks';
import { Reveal } from '../Reveal';

export function Process({ s, steps }: { s: SiteSettingsDto; steps: ProcessStepDto[] }) {
  if (!steps.length) return null;
  return (
    <section aria-labelledby="process-title" className="container-site pb-20 md:pb-[159px]">
      <div aria-hidden className="h-px bg-line" />
      <Reveal className="pt-16 md:pt-[87px]">
        <h2 id="process-title" className="display-xl max-w-[7.3em] text-strong">
          {s.processHeading}
        </h2>
        {s.processIntro && (
          <p className="mt-8 max-w-[672px] text-body text-graphite md:mt-[70px]">
            {s.processIntro}
          </p>
        )}
      </Reveal>
      <ProcessSteps steps={steps} />
      {s.processNote && (
        <p className="mt-10 text-center text-lead font-semibold text-graphite md:mt-[52px]">
          {s.processNote}
        </p>
      )}
    </section>
  );
}

/** Scroll distance (in viewport heights) the cards stay pinned for, per step. */
const STEP_VH = 0.35;
/** The last header turns orange a little before the pin lets go, so the full list holds a beat. */
const FILL_END = 0.9;

/**
 * The cards pin in the middle of the screen while the visitor scrolls, and orange sweeps across
 * the grey headers one card after another (left before right on a shared row), in reading
 * direction: left to right, right to left on Arabic pages. Once the last one is orange the page
 * moves on; scrolling back up leaves them orange. Reduced-motion visitors get orange
 * headers and no pin.
 */
function ProcessSteps({ steps }: { steps: ProcessStepDto[] }) {
  const reduce = useReducedMotion();
  const pinned = !reduce;
  const progress = useMotionValue(0);
  const { outer, inner, outerStyle, innerStyle } = useScrollPin<HTMLOListElement>({
    enabled: pinned,
    distance: (vh) => steps.length * STEP_VH * vh,
    align: 'center',
    // Plays once: scrolling back up leaves the headers orange.
    onProgress: (p) => progress.set(Math.max(progress.get(), p / FILL_END)),
  });
  return (
    <div ref={outer} style={outerStyle} className="mt-8 md:mt-[45px]">
      <ol
        ref={inner}
        style={innerStyle}
        className={cn(
          'grid gap-5 sm:grid-cols-2 md:gap-x-[38px] md:gap-y-[35px]',
          pinned && 'sticky',
        )}
      >
        {steps.map((step, i) => (
          <ProcessCard
            key={step.id}
            step={step}
            index={i}
            total={steps.length}
            progress={progress}
          />
        ))}
      </ol>
    </div>
  );
}

function ProcessCard({
  step,
  index,
  total,
  progress,
}: {
  step: ProcessStepDto;
  index: number;
  total: number;
  progress: MotionValue<number>;
}) {
  const reduce = useReducedMotion();
  // Each card owns an equal slice of the scroll range and the orange sweeps across it.
  const fill = useTransform(progress, [index / total, (index + 1) / total], [0, 1]);
  return (
    <li className="overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#FAFAFA_0%,#FFFFFF_100%)] dark:bg-none dark:bg-card shadow-[0_30px_50px_-20px_rgb(30_30_30/0.28)] transition-transform duration-300 hover:-translate-y-1 md:rounded-[40px]">
      <h3 className="relative z-10 flex h-[76px] items-center gap-2.5 bg-silver px-6 text-white dark:bg-card-raised shadow-[0_18px_28px_-14px_rgb(0_0_0/0.22)] md:h-[99px] md:px-8">
        <motion.span
          aria-hidden
          className="absolute inset-0 origin-left bg-primary rtl:origin-right"
          style={{ scaleX: reduce ? 1 : fill }}
        />
        <span className="relative text-h2 leading-none font-extralight text-white/60 tabular-nums">
          {index + 1}
        </span>
        <span aria-hidden className="relative text-h2 leading-none font-extralight text-white/60">
          |
        </span>
        <span className="relative text-h2 leading-none font-bold">{step.title}</span>
      </h3>
      <p className="flex items-center px-6 py-6 text-body text-graphite md:min-h-[92px] md:px-[33px] md:py-4">
        {step.description}
      </p>
    </li>
  );
}
